import {omit} from 'lodash-es';
import moment from 'moment';

import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {
  AfriexProcessorTransaction,
  AfriexProcessorTxParams,
} from '@/screens/Processor/Transactions/types';
import type {
  AdminPaginatedEnvelope,
  AfriexAdminTransaction,
  AfriexOffsetPaginatedResponse,
  AfriexQueryParams,
  SettlementAdviceDownload,
} from '@/types';
import {parseBlobErrorBody} from '@/utils/downloadFile';

// A file the server streams back (a CSV export, a statement PDF).
const downloadBlob = async (url: string): Promise<Blob> => {
  try {
    const response = await axios.get<Blob>(url, {responseType: 'blob'});
    return response.data;
  } catch (error) {
    throw await parseBlobErrorBody(error);
  }
};

export const transactionService = {
  getTransactionList: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexAdminTransaction[]>> => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `/v2/admin/transactions?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexAdminTransaction[]>>(url);
    return response.data;
  },
  getUserTransactionList: async (
    userId: string,
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexAdminTransaction[]>> => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `/v2/admin/transactions/user/${userId}?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexAdminTransaction[]>>(url);
    return response.data;
  },
  // The whole filtered list as a CSV, streamed by the server. Only the
  // filters are sent: the server ignores paging for a download.
  downloadTransactionList: async (filters: AfriexQueryParams): Promise<Blob> =>
    downloadBlob(
      `/v2/admin/transactions?${toQueryString({...filters, action: 'DOWNLOAD'})}`,
    ),
  downloadUserTransactionList: async (
    userId: string,
    filters: AfriexQueryParams,
  ): Promise<Blob> =>
    downloadBlob(
      `/v2/admin/transactions/user/${userId}?${toQueryString({...filters, action: 'DOWNLOAD'})}`,
    ),
  // The user's account statement for the range as a PDF, built and streamed
  // by the server (User > Statement > Download).
  downloadUserStatement: async (
    userId: string,
    filters: AfriexQueryParams,
  ): Promise<Blob> =>
    downloadBlob(
      `/v2/admin/transactions/user/${userId}/statement?${toQueryString({...filters})}`,
    ),
  getTransaction: async ({
    id,
  }: {
    id: string;
  }): Promise<AfriexAdminTransaction> => {
    const url = `/v2/admin/transactions/${id}`;
    const response = await axios.get<AfriexAdminTransaction>(url);
    return response.data;
  },
  // Settlement & remittance advice for collection-wallet USD withdrawals.
  // Resolves to a short-lived presigned S3 URL; rejects with a 404
  // (BusinessTransactionNotFound) when the transaction has no advice.
  getTransactionSettlementAdvice: async (
    transactionId: string,
  ): Promise<SettlementAdviceDownload> => {
    const url = `/v2/admin/transactions/${transactionId}/settlement-advice`;
    const response = await axios.get<SettlementAdviceDownload>(url);
    return response.data;
  },
  getRewardTransactions: async (
    userId: string,
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexAdminTransaction[]>> => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `/v2/admin/transactions/rewarded/${userId}?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexAdminTransaction[]>>(url);
    return response.data;
  },
  getProcessorTransactions: async (
    filters: AfriexProcessorTxParams,
  ): Promise<AfriexOffsetPaginatedResponse<AfriexProcessorTransaction[]>> => {
    const defaultLimit = 10;
    const queryParams = {
      limit: 10,
      offset: 0,
    };
    // Map your filter fields to endpoint fields
    const mappedFilters = {...filters};
    // Handle pagination conversion (page → offset)
    if (filters.page !== undefined) {
      mappedFilters.offset =
        filters.page * (filters.limit || queryParams.limit);
      delete mappedFilters.page;
    }

    // Handle date conversion
    if (mappedFilters.fromDate) {
      mappedFilters.startDate = moment(mappedFilters.fromDate)
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ss.SSS');
      delete mappedFilters.fromDate;
    }

    if (mappedFilters.toDate) {
      mappedFilters.endDate = moment(mappedFilters.toDate)
        .endOf('day')
        .format('YYYY-MM-DDTHH:mm:ss.SSS');
      delete mappedFilters.toDate;
    }

    // Merge with defaults
    const params = {...queryParams, ...mappedFilters};
    const {processor, ...queryStringParams} = params;
    const urlParams = toQueryString(queryStringParams);
    const url = `/v2/admin/processors/${processor}/transactions?${urlParams}`;
    const response = await axios.get<AfriexProcessorTransaction[]>(url);

    // Extract current limit and offset values that were used in the request
    const currentLimit = params.limit || defaultLimit;
    const currentOffset = params.offset || 0;

    // Get the data
    const {data} = response;

    // Check if there are more pages based on the number of items returned
    const hasMore = Array.isArray(data) && data.length === currentLimit;
    const isEmpty = Array.isArray(data) && data.length === 0;

    return {
      data,
      pageInfo: {
        offset: currentOffset,
        limit: currentLimit,
        nextOffset:
          Array.isArray(data) && data.length > 0
            ? currentOffset + currentLimit
            : undefined,
        hasMore,
        isEmpty,
      },
    };
  },
  getSingleProcessorTransaction: async (
    processor: string,
    transactionId: string,
    queryParams?: Partial<AfriexProcessorTxParams>,
  ): Promise<AfriexProcessorTransaction> => {
    // Build the URL with the processor and transaction ID
    let url = `/v2/admin/processors/${processor}/transactions/${transactionId}`;

    // If there are additional query parameters, add them to the URL
    if (queryParams && Object.keys(queryParams).length > 0) {
      // Extract only the relevant query parameters (exclude pagination)
      const relevantParams = omit(queryParams, ['page', 'offset', 'limit']);

      // Add query parameters to the URL
      const urlParams = toQueryString(relevantParams);
      if (urlParams) {
        url += `?${urlParams}`;
      }
    }
    // Make the API request
    const response = await axios.get<AfriexProcessorTransaction>(url);
    return response.data;
  },
};
